/**
 * 図の隣に置く詳細ペインの外枠。右パネルの枠（見出しと ✕）の中に、導線・なぜ変えたか・中身を並べる。
 *
 * **枠は画面詳細の右パネルと同じ部品（`PanelFrame`）で描く**（Dhik 2026-09-30「業務フローの業務を
 * クリックしたときに右側に出してほしい」）。右パネルの見た目と閉じ方（✕ ・ Esc ・ もう一度押す）を
 * 画面ごとに変えない。差分の印（追加 ／ 変更 ／ 削除）は図の箱の色と印で読めるので出さない。
 *
 * **なぜ変えたかは導線のすぐ下に置く。** 案件で見る人が一番知りたいのは「なぜ変わったか」で
 * ある。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図の詳細ペインの外枠として、右パネルの枠に見出し・導線・なぜ変えたか・中身を並べる。
 * @graph-connects PanelFrame [calls] 右パネルの枠
 * @graph-connects FlowWhyChangedSection [calls] なぜ変えたかの節
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FIELD_HEAD_CLASS, ITEM_CLASS, PanelFrame } from '../screen-detail-panel.js';

/** 業務フローの右パネルの枠に付ける印。画面詳細の `data-screen-panel` と分ける。 @graph-connects none */
const FLOW_PANEL_DATASET = { 'data-flow-panel': '' } as const;

/**
 * `FlowWhyChangedSection` の props。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business なぜ変えたかの節に並べる記述を表す。
 * @graph-connects none
 */
export interface FlowWhyChangedSectionProps {
  /** この節点をなぜそう変えたか。1 件も無ければ節ごと出さない。 */
  entries: readonly string[];
}

/**
 * この節点をなぜ変えたか。1 件も無ければ節ごと出さない。
 *
 * 「理由なし」と書かないのは、ここが空になる理由が 2 通りあるためである — 案件から入って
 * いない（そもそも差分を見ていない）か、この節点をこの案件が触っていないか。どちらも
 * 「理由が記録されていない変更がある」という意味ではないので、節を出さないほうが正しい。
 *
 * @param props なぜ変えたかの記述
 * @returns なぜ変えたかの節。1 件も無ければ何も描かない
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選択した節点をこの案件がなぜ変えたかを一覧する。
 * @graph-connects none
 */
export function FlowWhyChangedSection({ entries }: FlowWhyChangedSectionProps) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;

  return (
    <section aria-label={t('userStoryFlow.detailReasonTitle')} data-flow-detail="reasons">
      <h3 className={FIELD_HEAD_CLASS}>{t('userStoryFlow.detailReasonTitle')}</h3>
      <ul>
        {entries.map((entry) => (
          <li key={entry} className={`${ITEM_CLASS} text-[var(--color-text-primary)]`}>
            {entry}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * `FlowDetailShell` の props。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 詳細ペインの外枠に渡す見出し・閉じる口・導線・なぜ変えたか・中身を表す。
 * @graph-connects none
 */
export interface FlowDetailShellProps {
  /** 見出し（選んだ節点の名前）。 */
  heading: ReactNode;
  /** 右パネルの読み上げの名前。 */
  label: string;
  /** ✕ を押したときに伝える。 */
  onClose: () => void;
  /** 見出しのすぐ下に置く導線（業務詳細へのボタンなど）。 */
  lead?: ReactNode;
  /** この節点をなぜそう変えたか。 */
  whyChanged: readonly string[];
  /** なぜ変えたかの下に置く中身（業務ルールの並びなど）。 */
  children?: ReactNode;
}

/**
 * 詳細ペインの外枠を描く。
 *
 * @param props 見出し・閉じる口・導線・なぜ変えたか・中身
 * @returns 右パネルの枠に見出し・導線・なぜ変えたか・中身を並べた詳細
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選択した節点の見出しを右パネルの枠に載せ、導線・なぜ変えたか・中身をこの順に並べて描く。
 * @graph-connects PanelFrame [calls] 右パネルの枠
 * @graph-connects FlowWhyChangedSection [calls] なぜ変えたかの節
 */
export function FlowDetailShell({
  heading,
  label,
  onClose,
  lead,
  whyChanged,
  children,
}: FlowDetailShellProps) {
  return (
    <PanelFrame title={heading} ariaLabel={label} dataset={FLOW_PANEL_DATASET} onClose={onClose}>
      <div className="flex flex-col" data-flow-detail="shell">
        {lead}
        <FlowWhyChangedSection entries={whyChanged} />
        {children}
      </div>
    </PanelFrame>
  );
}
